import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildQueryString } from '@/lib/api/client';
import { recipientsFetch } from '@/lib/recipients/api';
import { toastRecipientRefusal } from '@/lib/recipients/refusal-codes';
import { useTranslation } from '@/lib/i18n';
import { toast } from 'sonner';
import { tagKeys, recipientKeys } from '@/hooks/api/query-keys';
import type { BulkTarget } from '@/lib/types/bulk';
import type { PaginatedResult } from '@/hooks/api/pagination';

// ============ Types ============

export interface Tag {
  id: string;
  name: string;
  color?: string;
  recipientCount: number;
  createdAt: string;
  updatedAt: string;
}

type TagBulkTarget = { tagIds: string[] } & (BulkTarget | { recipientIds: string[] });

function toTagBulkTarget(data: TagBulkTarget): { tagIds: string[] } & BulkTarget {
  if ('recipientIds' in data) {
    const { recipientIds, ...rest } = data;
    return { ...rest, ids: recipientIds };
  }
  return data;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============ Query Hooks ============

/**
 * Every tag in the workspace as a flat array — genuinely unpaginated. Used in
 * filter dropdowns, pickers and every name → id resolution.
 *
 * `GET /recipients/tags` is server-paginated (default limit 25, max 100). This
 * hook used to take that single default page, so a workspace's 26th tag
 * onward did not exist for the app: the profile page and org chart map names
 * to ids through it and `POST /:id/tags` REPLACES the set, so any tag edit
 * silently removed those tags (audit 2026-09-25 FE2). Pages through every
 * result at the endpoint's max page size, as `useGroups` does.
 */
export function useTags(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: tagKeys.list(filter),
    queryFn: () => fetchAllTags(filter),
  });
}

/** Every page of `GET /recipients/tags` — the query behind {@link useTags}. */
async function fetchAllTags(filter?: Record<string, unknown>): Promise<Tag[]> {
  const LIMIT = 100; // /recipients/tags max page size
  const all: Tag[] = [];
  let page = 1;
  for (let i = 0; i < 100; i++) {
    const qs = buildQueryString({ ...(filter ?? {}), page, limit: LIMIT });
    const result = await recipientsFetch<{ data: Tag[]; total?: number } | Tag[]>(
      `tags${qs}`,
    );
    // A bare array carries no paging envelope — it is the whole set.
    if (Array.isArray(result)) {
      all.push(...result);
      break;
    }
    const batch = result?.data ?? [];
    all.push(...batch);
    // Stop on the last (partial) page, or once we've collected `total`.
    if (batch.length < LIMIT) break;
    if (typeof result.total === 'number' && all.length >= result.total) break;
    page += 1;
  }
  return all;
}

export interface ResolvedTagIds {
  /** Ids for the names that matched, in the order the names were given. */
  ids: string[];
  /** Names no workspace tag matches, even after re-reading the list. */
  unresolved: string[];
}

/**
 * A name matches the tag of that exact name, else the ONE tag whose name
 * differs only in case: TagPicker accepts "vip" as the existing "VIP" (its
 * new-tag check ignores case), so the name it hands back can be either
 * spelling. Two tags that differ only in case make such a name ambiguous: it
 * stays unresolved rather than guessing.
 */
function matchTagIds(tags: Tag[], names: string[]): ResolvedTagIds {
  const exact = new Map<string, string>();
  const folded = new Map<string, string | null>();
  for (const tag of tags) {
    if (!tag?.id || !tag.name) continue;
    exact.set(tag.name, tag.id);
    const key = tag.name.toLowerCase();
    folded.set(key, folded.has(key) ? null : tag.id);
  }
  const ids: string[] = [];
  const unresolved: string[] = [];
  for (const name of names) {
    const id = exact.get(name) ?? folded.get(name.toLowerCase()) ?? undefined;
    if (id) ids.push(id);
    else unresolved.push(name);
  }
  return { ids, unresolved };
}

/**
 * Wave-3 item 8 (audit 2026-09-25) — resolve the tag NAMES a tag editor hands
 * back to the ids `POST /recipients/:id/tags` takes, and say which names could
 * not be resolved instead of dropping them.
 *
 * TagPicker creates an unknown name and calls onChange the moment the create
 * resolves; useCreateTag's onSuccess does not await its invalidation, and the
 * handler it calls was captured before the create. Every name → id map built at
 * render therefore missed the new tag: the profile page and org chart dropped
 * it with `.filter(Boolean)` (and POST /:id/tags REPLACES the set), and the
 * list page tried to create it a second time and got a 409.
 *
 * This reads the cached list at CALL time and, when a name is missing, reads
 * the list again once (joining the refetch the create's invalidation already
 * started). It never creates a tag and never throws: a failed re-read leaves
 * the missing names unresolved, and the caller refuses to save.
 */
export function useResolveTagIds(): (names: string[]) => Promise<ResolvedTagIds> {
  const queryClient = useQueryClient();
  return useCallback(
    async (names: string[]) => {
      const cached = queryClient.getQueryData<Tag[]>(tagKeys.list(undefined));
      const first = matchTagIds(Array.isArray(cached) ? cached : [], names);
      if (first.unresolved.length === 0) return first;
      try {
        const fresh = await queryClient.fetchQuery({
          queryKey: tagKeys.list(undefined),
          queryFn: () => fetchAllTags(undefined),
          staleTime: 0,
        });
        return matchTagIds(Array.isArray(fresh) ? fresh : [], names);
      } catch {
        return first;
      }
    },
    [queryClient],
  );
}

/**
 * name → color hex map for the workspace's tags. Recipient surfaces (table
 * rows, tooltips, pickers) only carry tag NAMES, so they look the color up
 * here to render each chip in its assigned color. Shares the cached useTags()
 * query — no extra request per consumer.
 */
export function useTagColorMap(): Map<string, string | undefined> {
  const { data } = useTags();
  return useMemo(() => {
    const list: Tag[] = Array.isArray(data) ? data : [];
    const map = new Map<string, string | undefined>();
    for (const t of list) map.set(t.name, t.color ?? undefined);
    return map;
  }, [data]);
}

/** Paginated — returns { data, total, page, limit, totalPages }. Used by the tags table. */
export function useTagsPaginated(filter?: { page?: number; limit?: number; search?: string; status?: string }) {
  const defaultLimit = filter?.limit ?? 25;
  return useQuery({
    queryKey: [...tagKeys.lists(), 'paginated', filter],
    queryFn: async () => {
      const qs = filter ? buildQueryString(filter as Record<string, unknown>) : '';
      return recipientsFetch<PaginatedResponse<Tag> | Tag[]>(`tags${qs}`);
    },
    select: (raw): PaginatedResult<Tag> => {
      if (Array.isArray(raw)) {
        return { data: raw, total: raw.length, page: 1, limit: raw.length || defaultLimit, totalPages: 1 };
      }
      const r = raw as PaginatedResponse<Tag>;
      const limit = r.limit ?? defaultLimit;
      const total = r.total ?? 0;
      return {
        data: r.data ?? [],
        total,
        page: r.page ?? 1,
        limit,
        totalPages: r.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

// ============ Mutation Hooks ============

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color?: string }) =>
      recipientsFetch<Tag>('tags', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string } }) =>
      recipientsFetch<Tag>(`tags/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<void>(`tags/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * F17 (audit 2026-09-25) — delete many tags in ONE request
 * (POST /recipients/tags/bulk-delete → { deleted }), revalidating once. The
 * Tags page used to send one DELETE per tag through useDeleteTag.
 */
export function useBulkDeleteTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      recipientsFetch<{ deleted: number }>('tags/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

export function useMergeTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { sourceIds: string[]; targetName: string }) =>
      recipientsFetch<Tag>('tags/merge', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    // No toast here: its caller (the Tags page) reports a refusal once, with
    // the server's reason — a hook toast on top said it twice.
  });
}

export function useAssignTagToRecipients() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: TagBulkTarget) =>
      recipientsFetch<{ assigned: number }>('tags/assign-to-recipients', {
        method: 'POST',
        body: JSON.stringify(toTagBulkTarget(data)),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    // Said here, once, in the reader's language (refusal-codes.ts), so it is
    // said even when the page has gone before the answer. This toasted the
    // API's English, and the page added a second, title-only message.
    onError: (err: Error) =>
      toastRecipientRefusal(t, 'Adding tags to recipients', 'toast.failedBulkAddTags', err),
  });
}

export function useRemoveTagFromRecipients() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: TagBulkTarget) =>
      recipientsFetch<{ removed: number }>('tags/remove-from-recipients', {
        method: 'POST',
        body: JSON.stringify(toTagBulkTarget(data)),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    // Said here, once, in the reader's language (refusal-codes.ts), so it is
    // said even when the page has gone before the answer.
    onError: (err: Error) =>
      toastRecipientRefusal(t, 'Removing tags from recipients', 'toast.failedBulkRemoveTags', err),
  });
}

// Aliases for bulk tag operations used by page-level hooks
export const useBulkAssignTagsToRecipients = useAssignTagToRecipients;
export const useBulkRemoveTagsFromRecipients = useRemoveTagFromRecipients;
